import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  RefreshCw,
  Search,
  Download,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  Filter,
  Clock,
} from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const LEVEL_STYLES = {
  error: { bg: "bg-red-50", text: "text-red-800", icon: XCircle },
  warning: { bg: "bg-yellow-50", text: "text-yellow-800", icon: AlertTriangle },
  info: { bg: "bg-blue-50", text: "text-blue-800", icon: Info },
  debug: { bg: "bg-gray-50", text: "text-gray-600", icon: Info },
};

const SERVICES = [
  { value: "", label: "All Services" },
  { value: "hyveai-auth", label: "Auth Service" },
  { value: "training-prep", label: "Training Prep" },
  { value: "training-prep-data", label: "Data Preparation" },
  { value: "model-validator", label: "Model Validator" },
  { value: "model-benchmark", label: "Model Benchmark" },
  { value: "dashboard", label: "Dashboard" },
  { value: "job-submission", label: "Job Submission API" },
];

const TIME_RANGES = [
  { value: "15m", label: "Last 15 min" },
  { value: "1h", label: "Last 1 hour" },
  { value: "6h", label: "Last 6 hours" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
];

const LogEntry = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const level = (entry.level || "info").toLowerCase();
  const style = LEVEL_STYLES[level] || LEVEL_STYLES.info;
  const Icon = style.icon;

  return (
    <div
      className={`${style.bg} border-l-4 ${
        level === "error"
          ? "border-red-500"
          : level === "warning"
            ? "border-yellow-500"
            : "border-blue-300"
      } px-3 py-2 mb-1 cursor-pointer hover:opacity-90 transition-opacity font-mono text-xs`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 mt-0.5 ${style.text} flex-shrink-0`} />
        <span className="text-gray-400 flex-shrink-0 w-44">
          {new Date(entry.ts).toLocaleString()}
        </span>
        <span
          className={`uppercase font-semibold w-14 flex-shrink-0 ${style.text}`}
        >
          {level}
        </span>
        {entry.service && (
          <span className="text-purple-600 flex-shrink-0 w-28 truncate">
            [{entry.service}]
          </span>
        )}
        <span className="text-gray-800 flex-1 truncate">{entry.msg}</span>
        {entry.trace_id && (
          <span className="text-gray-400 flex-shrink-0 ml-2">
            {entry.trace_id.slice(0, 8)}
          </span>
        )}
      </div>
      {expanded && (
        <div className="mt-2 ml-6 p-2 bg-white rounded text-xs space-y-1">
          {entry.trace_id && (
            <div>
              <span className="text-gray-500">Trace ID:</span>{" "}
              <span className="font-medium">{entry.trace_id}</span>
            </div>
          )}
          {entry.logger && (
            <div>
              <span className="text-gray-500">Logger:</span> {entry.logger}
            </div>
          )}
          {entry.method && (
            <div>
              <span className="text-gray-500">Request:</span> {entry.method}{" "}
              {entry.path} → {entry.status_code}{" "}
              {entry.duration_ms && `(${entry.duration_ms}ms)`}
            </div>
          )}
          {entry.facility_id && (
            <div>
              <span className="text-gray-500">Facility:</span>{" "}
              {entry.facility_id}
            </div>
          )}
          {entry.exception && (
            <pre className="mt-1 p-2 bg-red-50 text-red-700 rounded overflow-x-auto whitespace-pre-wrap">
              {entry.exception}
            </pre>
          )}
          <details className="mt-1">
            <summary className="text-gray-400 cursor-pointer">Raw JSON</summary>
            <pre className="mt-1 p-2 bg-gray-100 rounded overflow-x-auto">
              {JSON.stringify(entry, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default function LogViewer() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  // Filters
  const [service, setService] = useState("");
  const [level, setLevel] = useState("");
  const [search, setSearch] = useState("");
  const [timeRange, setTimeRange] = useState("1h");
  const [limit, setLimit] = useState(200);

  const getAuthHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token],
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ range: timeRange, limit });
      if (service) params.set("service", service);
      if (level) params.set("level", level);
      if (search) params.set("search", search);

      const res = await fetch(`${AUTH_URL}/logs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data.entries || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, service, level, search, timeRange, limit, getAuthHeaders]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLogs, 5000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchLogs]);

  const handleExport = () => {
    const blob = new Blob([logs.map((l) => JSON.stringify(l)).join("\n")], {
      type: "application/x-ndjson",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hyveai-logs-${new Date().toISOString().slice(0, 16)}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const errorCount = logs.filter(
    (l) => (l.level || "").toLowerCase() === "error",
  ).length;
  const warnCount = logs.filter(
    (l) => (l.level || "").toLowerCase() === "warning",
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Service Logs</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <SecondaryButton
            onClick={handleExport}
            disabled={logs.length === 0}
            icon={Download}
          >
            Export
          </SecondaryButton>
          <PrimaryButton
            onClick={fetchLogs}
            disabled={loading}
            icon={RefreshCw}
          >
            {loading ? "Loading..." : "Refresh"}
          </PrimaryButton>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-lg shadow p-3 text-center">
          <div className="text-2xl font-bold text-gray-800">{logs.length}</div>
          <div className="text-xs text-gray-500">Total Entries</div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{errorCount}</div>
          <div className="text-xs text-gray-500">Errors</div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 text-center">
          <div className="text-2xl font-bold text-yellow-600">{warnCount}</div>
          <div className="text-xs text-gray-500">Warnings</div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 text-center">
          <div className="text-2xl font-bold text-green-600">
            {logs.length - errorCount - warnCount}
          </div>
          <div className="text-xs text-gray-500">Info/Debug</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-gray-400" />

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          >
            {TIME_RANGES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          >
            {SERVICES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
          >
            <option value="">All Levels</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>

          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-2 top-2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded pl-8 pr-3 py-1.5 text-sm"
            />
          </div>

          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm"
          >
            <option value={100}>100 lines</option>
            <option value={200}>200 lines</option>
            <option value={500}>500 lines</option>
            <option value={1000}>1000 lines</option>
          </select>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700">
            Failed to load logs: {error}
          </span>
        </div>
      )}

      {/* Log entries */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gray-900 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-300 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>
              {logs.length} entries ({timeRange})
            </span>
            {autoRefresh && (
              <span className="text-green-400 animate-pulse">LIVE</span>
            )}
          </div>
        </div>
        <div
          className="max-h-[600px] overflow-y-auto"
          style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}
        >
          {logs.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-400">
              No log entries found for the selected filters.
            </div>
          )}
          {logs.map((entry, i) => (
            <LogEntry key={`${entry.ts}-${i}`} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}
